import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBullseye,
  FaGift,
  FaHistory,
  FaMoneyBillWave,
  FaSignOutAlt,
  FaSyncAlt,
  FaTrophy,
  FaUserFriends,
  FaUsers,
  FaWallet
} from 'react-icons/fa';
import logo from '../../for-web-logo.png';
import { AuthContext } from '../../contexts/AuthContext';
import paymentService from '../../services/paymentService';

const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [balance, setBalance] = useState(0);

  const roleValue =
    user?.role ??
    user?.Role ??
    user?.user?.role ??
    user?.user?.Role ??
    '';

  const isAdmin = String(roleValue).trim().toLowerCase() === 'admin';
  const username =
    user?.username ??
    user?.Username ??
    user?.user?.username ??
    user?.user?.Username ??
    'User';

  useEffect(() => {
    if (!user) return;

    const fetchBalance = async () => {
      try {
        const data = await paymentService.getBalance();
        setBalance(Number(data || 0));
      } catch (fetchError) {
        console.error('Error fetching balance:', fetchError);
      }
    };

    fetchBalance();
    window.addEventListener('balanceUpdated', fetchBalance);
    return () => window.removeEventListener('balanceUpdated', fetchBalance);
  }, [user]);

  const navigationItems = useMemo(() => {
    if (!user) {
      return [
        { to: '/login', label: 'Login', icon: <FaUsers /> },
        { to: '/register', label: 'Register', icon: <FaGift /> }
      ];
    }

    if (isAdmin) {
      return [
        { to: '/leagues', label: 'Leagues', icon: <FaBullseye /> },
        { to: '/contests', label: 'Contests', icon: <FaTrophy /> },
        { to: '/payment-requests', label: 'Payment Requests', icon: <FaMoneyBillWave /> },
        { to: '/admin/payment-accounts', label: 'Payment Accounts', icon: <FaWallet /> },
        { to: '/admin/spin-game', label: 'Spin Settings', icon: <FaSyncAlt /> },
        { to: '/admin/referrals', label: 'Referrals', icon: <FaGift /> },
        { to: '/users', label: 'Users', icon: <FaUserFriends /> },
        { to: '/admin/withdrawals', label: 'Withdrawals', icon: <FaHistory /> }
      ];
    }

    return [
      { to: '/dashboard', label: 'Dashboard', icon: <FaBullseye /> },
      { to: '/contests', label: 'Contests', icon: <FaTrophy /> },
      { to: '/fantasy-teams', label: 'Fantasy Teams', icon: <FaUsers /> },
      { to: '/spin', label: 'Cash Spin', icon: <FaSyncAlt /> },
      { to: '/payments', label: 'Payments', icon: <FaMoneyBillWave /> },
      { to: '/withdrawals', label: 'Withdrawals', icon: <FaHistory /> },
      { to: '/referrals', label: 'Referrals', icon: <FaGift /> }
    ];
  }, [isAdmin, user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (to) => location.pathname === to || (to !== '/' && location.pathname.startsWith(`${to}/`));

  const NavItems = ({ mobile = false }) => (
    <div className={mobile ? 'space-y-2' : 'space-y-1'}>
      {navigationItems.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          onClick={() => {
            if (mobile) setIsMenuOpen(false);
          }}
          className={`flex items-center rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
            isActive(item.to)
              ? 'bg-slate-900 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
        >
          <span className="mr-3 text-base">{item.icon}</span>
          {item.label}
        </Link>
      ))}
    </div>
  );

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Ideal11 Logo" className="h-10 w-auto" />
          </Link>

          <div className="flex items-center gap-3">
            {user && (
              <div className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                Play Rs {balance.toFixed(2)}
              </div>
            )}
            <button
              className="rounded-xl border border-slate-200 px-3 py-2 text-slate-700"
              onClick={() => setIsMenuOpen((value) => !value)}
            >
              Menu
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="border-t border-slate-200 px-4 py-4">
            <NavItems mobile />
            {user && (
              <button
                onClick={handleLogout}
                className="mt-3 flex w-full items-center rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700"
              >
                <FaSignOutAlt className="mr-3" />
                Logout
              </button>
            )}
          </div>
        )}
      </header>

      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[290px] border-r border-slate-200 bg-white md:flex md:flex-col">
        <div className="border-b border-slate-200 px-6 py-6">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Ideal11 Logo" className="h-12 w-auto" />
          </Link>
          {user && !isAdmin && (
            <div className="mt-6 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-4 text-white">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-300">Playable Wallet</p>
              <p className="mt-2 text-2xl font-extrabold">Rs {balance.toFixed(2)}</p>
              <p className="mt-3 text-xs text-slate-300">Referral code: {user?.referralCode || 'Loading'}</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5">
          <NavItems />
        </div>

        {user && (
          <div className="border-t border-slate-200 px-4 py-4">
            <div className="mb-3 rounded-2xl bg-slate-50 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">{username}</p>
              <p className="mt-1 text-xs text-slate-500">{isAdmin ? 'Admin Panel' : 'Player Account'}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex w-full items-center rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              <FaSignOutAlt className="mr-3" />
              Logout
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Header;
